import { EventIds } from 'constants/eventIds';
import { useCallback, useRef } from 'react';
import { fetchWithCommonHeaders } from '@landr/core';
import { Pack } from 'types/generated/samplesApi';
import { log } from 'utils/log';
import { CONFIG } from 'utils';
import { fetchErrorHandler } from 'helpers/fetchErrorHandler/fetchErrorHandler';

const ArtworkUrlGuidPlaceholder = 'GUID';

export type PackSuggestionRequiredFields = Pick<
    Pack,
    'guid' | 'slug' | 'name' | 'artworkUrl'
>;

let packs: PackSuggestionRequiredFields[] = [];
let isLoading = false;
let hasError = false;
let loadPacksPromise: Promise<Response> | undefined = undefined;

export const usePacks = () => {
    // Use AbortController to cancel requests when component unmounts
    const abortController = useRef<AbortController>();

    const load = useCallback(async () => {
        if (loadPacksPromise) {
            return;
        }

        // Cancel any previous request
        if (abortController.current) {
            abortController.current.abort();
        }

        // Create new abort controller for this request
        abortController.current = new AbortController();

        try {
            isLoading = true;
            loadPacksPromise = fetchWithCommonHeaders(
                `${CONFIG.VITE_API_URL}/packs/suggestions`,
                {
                    method: 'GET',
                    headers: {
                        accept: 'application/json',
                    },
                    signal: abortController.current.signal,
                },
                {
                    log,
                    retryCount: 3,
                },
            );

            const response = await loadPacksPromise;
            const data: { suggestions: Pack[]; artworkUrl: string } =
                await response.json();

            const artworkUrlBase = data.artworkUrl;

            packs = data.suggestions.map((pack) => ({
                ...pack,
                artworkUrl: artworkUrlBase.replace(
                    ArtworkUrlGuidPlaceholder,
                    pack.guid,
                ),
            }));
        } catch (error) {
            hasError = fetchErrorHandler({
                errorMessage: 'Failed to fetch packs',
                eventId: EventIds.PackSuggestionsError,
                error,
            });
        } finally {
            isLoading = false;
        }
    }, []);

    return {
        load,
        packs,
        hasError,
        isLoading,
    };
};
